/**
 * Icon names used across the editor UI.
 *
 * Components pick an icon by reading `ICONS.<group>.<name>` instead of
 * typing a raw icon string. Groups match where the icon shows up, like
 * `headers`, `toolbar`, `layerActions`, and so on. Most values are
 * Material icon names; a few (prefixed `mdi-`) come from the MDI set.
 */
export const ICONS = {
  app: {
    upload: 'upload',
    image: 'image',
    text: 'title',
    currency: 'attach_money',
    percent: 'percent',
    star: 'star',
    color: 'palette'
  },
  uploadProgress: {
    upload: 'cloud_upload',
    close: 'close',
    slow: 'signal_cellular_alt_2_bar'
  },
  headers: {
    cloudCheck: 'cloud_done',
    save: 'save',
    undo: 'undo',
    redo: 'redo',
    edit: 'edit',
    share: 'share',
    download: 'download',
    publish: 'arrow_circle_up',
    panel: 'dock_to_right'
  },
  // The platform's own rail icons, so the editor's copy matches it.
  appNav: {
    templates: 'mdi-folder-outline',
    feeds: 'mdi-animation-outline',
    exports: 'mdi-upload-outline',
    search: 'mdi-magnify'
  },
  toolbar: {
    size: 'aspect_ratio',
    frame: 'crop_free',
    elements: 'category',
    attributes: 'conversion_path',
    shapes: 'crop_square',
    text: 'text_fields',
    image: 'image',
    video: 'movie',
    audio: 'music_note',
    brandKit: 'palette',
    templates: 'team_dashboard',
    upload: 'upload',
    fonts: 'font_download',
    layers: 'layers'
  },
  expansionPanel: {
    expanded: 'keyboard_arrow_down',
    collapse: 'chevron_forward'
  },
  textToolbar: {
    bold: 'format_bold',
    italic: 'format_italic',
    underline: 'format_underlined',
    strikethrough: 'format_strikethrough',
    alignLeft: 'format_align_left',
    alignCenter: 'format_align_center',
    alignRight: 'format_align_right',
    alignJustify: 'format_align_justify',
    textModeWrap: 'wrap_text',
    textModeAutoFit: 'fit_screen',
    textModeAutoWidth: 'swap_horiz',
    decreaseFontSize: 'remove',
    increaseFontSize: 'add',
    formatCurrency: 'attach_money',
    formatPercentage: 'percent',
    revertFormat: 'undo'
  },
  imageToolbar: {
    borderRadius: 'rounded_corner',
    flipHorizontal: 'flip',
    shadow: 'ev_shadow',
    fit: 'fit_screen',
    fill: 'crop_landscape',
    stretch: 'aspect_ratio',
    original: 'crop_original',
    rotate3D: 'mdi-rotate-3d'
  },
  aiToolbar: {
    ai: 'auto_awesome',
    reframe: 'crop_free',
    removeBackground: 'background_replace',
    edit: 'edit',
    studio: 'apps',
    layerize: 'layers',
    autoDetect: 'auto_fix_high',
    boxSelect: 'select',
    lasso: 'gesture',
    unselectAll: 'deselect',
    merge: 'filter_none',
    unmerge: 'content_copy',
    brush: 'brush',
    erase: 'ink_eraser',
    upscale: 'open_in_full',
    adapt: 'fit_screen',
    expand: 'open_in_full',
    cancel: 'close',
    send: 'arrow_upward'
  },
  shapeToolbar: {
    strokeWidth: 'line_weight',
    strokeSettings: 'mdi-format-line-style',
    joinMiter: 'square',
    joinRound: 'circle',
    joinBevel: 'hexagon'
  },
  elementToolbar: {
    mask: 'crop',
    enlarge: 'aspect_ratio',
    auto: 'space_dashboard',
    autoHorizontal: 'view_week',
    autoVertical: 'view_agenda',
    autoGap: 'space_bar',
    autoPaddingX: 'border_horizontal',
    autoPaddingY: 'border_vertical',
    individualPadding: 'padding',
    autoDisable: 'block',
    autoMenuOpen: 'keyboard_arrow_down',
    autoMenuClose: 'keyboard_arrow_up',
    crossStartH: 'align_vertical_top',
    crossCenterH: 'align_vertical_center',
    crossEndH: 'align_vertical_bottom',
    crossStartV: 'align_horizontal_left',
    crossCenterV: 'align_horizontal_center',
    crossEndV: 'align_horizontal_right',
    borderRadius: 'rounded_corner',
    wrapInFrame: 'space_dashboard'
  },
  timeline: {
    animate: 'animation',
    block: 'motion_blur',
    expandUp: 'keyboard_arrow_up',
    collapseDown: 'keyboard_arrow_down',
    // MDI set — solid glyphs, matches the canvas playback pill.
    play: 'mdi-play',
    pause: 'mdi-pause',
    stop: 'mdi-stop',
    loop: 'mdi-sync',
    directionIn: 'arrow_forward',
    directionOut: 'arrow_back',
    animationShown: 'visibility',
    animationHidden: 'visibility_off'
  },
  variantGrid: {
    format: 'text_fields',
    addAttribute: 'add_circle_outline',
    // MDI set — the Material Symbols table icons have no "add row" variant.
    addVariant: 'mdi-table-large-plus',
    download: 'download',
    close: 'close',
    copy: 'content_copy',
    delete: 'delete',
    dropdown: 'keyboard_arrow_down'
  },
  common: {
    opacity: 'tonality',
    rotate: 'rotate_right',
    fitToScreen: 'fullscreen',
    link: 'link',
    linkOff: 'link_off',
    linkAttribute: 'conversion_path',
    mosaicView: 'view_quilt',
    variantView: 'grid_view',
    kebab: 'more_vert'
  },
  aiTextBar: {
    write: 'edit_square',
    translate: 'translate',
    refine: 'auto_fix_high',
    submit: 'arrow_forward',
    cancel: 'close',
    prev: 'chevron_left',
    next: 'chevron_right',
    swap: 'sync',
    grammarOk: 'check_circle'
  },
  layerActions: {
    delete: 'delete',
    lock: 'lock',
    unlock: 'lock_open',
    moveUp: 'arrow_upward',
    moveDown: 'arrow_downward',
    moveToFront: 'vertical_align_top',
    moveToBottom: 'vertical_align_bottom',
    visible: 'visibility',
    hidden: 'visibility_off',
    cut: 'content_cut',
    copy: 'content_copy',
    paste: 'content_paste',
    replace: 'find_replace',
    duplicate: 'add_to_photos',
    group: 'layers',
    enter: 'login',
    ungroup: 'layers_clear'
  },
  sizeToolbar: {
    add: 'add',
    swap: 'swap_horiz',
    delete: 'delete'
  },
  threeD: {
    back: 'chevron_left',
    upload: 'upload',
    horizontal: 'mdi-rotate-360',
    vertical: 'mdi-restore',
    zoom: 'mdi-arrow-expand-all',
    reset: 'mdi-undo'
  },
  attributeImport: {
    csv: 'csv',
    feed: 'conversion_path',
    create: 'mdi-star-box-multiple-outline'
  },
  export: {
    formats: {
      png: 'image',
      jpeg: 'image',
      svg: 'image',
      pdf: 'description',
      mp4: 'smart_display',
      gif: 'gif_box'
    },
    sizeIndicator: 'desktop_windows',
    info: 'info',
    localDestination: 'computer',
    navPrev: 'chevron_left',
    navNext: 'chevron_right',
    arrow: 'chevron_right',
    back: 'arrow_back'
  }
};
